import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: Socket;
  private orderUpdates = new BehaviorSubject<any>(null);
  private currentSpecialId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second delay

  constructor() {
    console.log('Connecting to socket server:', environment.url);
    this.initializeSocket();
    this.setupVisibilityChangeListener();
  }

  private initializeSocket() {
    this.socket = io(environment.url, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      forceNew: true,
    });

    this.setupSocketListeners();
  }

  private setupSocketListeners() {
    this.socket.on('connect', () => {
      console.log('Connected to socket server with ID:', this.socket.id);
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection

      // Rejoin business room if we have a special ID
      if (this.currentSpecialId) {
        console.log(
          'Rejoining business room after reconnection:',
          this.currentSpecialId,
        );
        this.socket.emit('joinBusinessRoom', this.currentSpecialId);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    this.socket.on('orderUpdated', (data) => {
      console.log('Received order update:', data);
      this.orderUpdates.next(data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from socket server. Reason:', reason);

      // If disconnection was due to transport error or server issues, attempt reconnection
      if (
        reason === 'transport error' ||
        reason === 'ping timeout' ||
        reason === 'transport close'
      ) {
        this.attemptReconnection();
      }
    });

    // Debug socket state
    setInterval(() => {
      console.log('Socket state:', {
        connected: this.socket.connected,
        id: this.socket.id,
        specialId: this.currentSpecialId,
      });
    }, 5000);
  }

  private setupVisibilityChangeListener() {
    document.addEventListener('visibilitychange', () => {
      console.log('Visibility changed. Document hidden:', document.hidden);

      if (!document.hidden) {
        // App came back to foreground
        console.log('App came to foreground, checking socket connection...');

        // Check if socket is disconnected and attempt reconnection
        if (!this.socket.connected) {
          console.log('Socket disconnected, attempting reconnection...');
          this.attemptReconnection();
        } else {
          // Even if connected, ensure we're in the business room
          if (this.currentSpecialId) {
            console.log(
              'Ensuring business room connection:',
              this.currentSpecialId,
            );
            this.socket.emit('joinBusinessRoom', this.currentSpecialId);
          }
        }
      }
    });

    // Also handle page focus/blur events as additional safeguard
    window.addEventListener('focus', () => {
      console.log('Window focused, checking socket connection...');
      if (!this.socket.connected && this.currentSpecialId) {
        this.attemptReconnection();
      }
    });
  }

  private attemptReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    console.log(
      `Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`,
    );

    setTimeout(() => {
      if (!this.socket.connected) {
        console.log('Attempting manual reconnection...');
        this.socket.connect();
      }
    }, delay);
  }

  joinBusinessRoom(specialId: string) {
    console.log('Joining business room:', specialId);
    this.currentSpecialId = specialId; // Store the special ID for future reconnections

    // Function to emit join room event
    const joinRoom = () => {
      if (this.socket.connected) {
        console.log('Emitting joinBusinessRoom for:', specialId);
        this.socket.emit('joinBusinessRoom', specialId);
      } else {
        console.log(
          'Socket not connected, will join room after connection is established',
        );
      }
    };

    // If socket is already connected, join immediately
    if (this.socket.connected) {
      joinRoom();
    } else {
      // If not connected, wait for connection then join
      const onConnect = () => {
        console.log('Socket connected, joining business room:', specialId);
        this.socket.emit('joinBusinessRoom', specialId);
        this.socket.off('connect', onConnect); // Remove this specific listener
      };

      this.socket.on('connect', onConnect);
    }
  }

  getOrderUpdates() {
    return this.orderUpdates.asObservable();
  }

  disconnect() {
    if (this.socket) {
      console.log('Manually disconnecting socket');
      this.currentSpecialId = null;
      this.socket.disconnect();
    }
  }

  // Method to check connection status
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Method to reconnect socket
  reconnect() {
    console.log('Reconnecting socket...');
    this.reconnectAttempts = 0;
    if (this.socket) {
      this.socket.connect();
    }
  }
}
